import { useState, useEffect } from 'react';
import { Star, Clock, MapPin, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  getBusinesses, updateBusiness, getCities, getCategories
} from '@/lib/api';
import { toast } from 'sonner';

export default function AdminShowcase() {
  const [businesses, setBusinesses] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [bizRes, citiesRes, catsRes] = await Promise.all([
        getBusinesses({ status: 'active', limit: 1000 }),
        getCities(),
        getCategories()
      ]);
      setBusinesses(bizRes.data);
      setCities(citiesRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCityName = (id) => cities.find(c => c.id === id)?.name || '-';
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';

  const filteredBusinesses = selectedCity === 'all' 
    ? businesses 
    : businesses.filter(b => b.city_id === selectedCity);

  const anaVitrinItems = filteredBusinesses
    .filter(b => b.ana_vitrin)
    .sort((a, b) => (a.ana_vitrin_order || 0) - (b.ana_vitrin_order || 0));

  const donenVitrinItems = filteredBusinesses
    .filter(b => b.donen_vitrin)
    .sort((a, b) => (a.donen_vitrin_order || 0) - (b.donen_vitrin_order || 0));

  const handleToggleAnaVitrin = async (business) => {
    try {
      await updateBusiness(business.id, {
        ana_vitrin: !business.ana_vitrin,
        ana_vitrin_order: !business.ana_vitrin ? anaVitrinItems.length : 0
      });
      toast.success(business.ana_vitrin ? 'Ana Vitrin\'den çıkarıldı' : 'Ana Vitrin\'e eklendi');
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleToggleDonenVitrin = async (business) => {
    try {
      await updateBusiness(business.id, {
        donen_vitrin: !business.donen_vitrin,
        donen_vitrin_order: !business.donen_vitrin ? donenVitrinItems.length : 0
      });
      toast.success(business.donen_vitrin ? 'Dönen Vitrin\'den çıkarıldı' : 'Dönen Vitrin\'e eklendi');
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleMoveOrder = async (business, type, direction) => {
    const items = type === 'ana' ? [...anaVitrinItems] : [...donenVitrinItems];
    const orderField = type === 'ana' ? 'ana_vitrin_order' : 'donen_vitrin_order';
    const currentIndex = items.findIndex(b => b.id === business.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap
    const otherBusiness = items[newIndex];
    
    setSaving(true);
    try {
      await Promise.all([
        updateBusiness(business.id, { [orderField]: newIndex }),
        updateBusiness(otherBusiness.id, { [orderField]: currentIndex })
      ]);
      toast.success('Sıralama güncellendi');
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 page-enter" data-testid="admin-showcase">
      {/* City Filter */}
      <div className="flex items-center gap-4">
        <span className="text-muted-foreground">Şehir:</span>
        <Select value={selectedCity} onValueChange={setSelectedCity}>
          <SelectTrigger className="w-48 bg-card border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Şehirler</SelectItem>
            {cities.map(city => (
              <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Ana Vitrin */}
        <Card className="bg-card border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Ana Vitrin
              <Badge variant="secondary" className="ml-auto">{anaVitrinItems.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {anaVitrinItems.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                Ana Vitrin'de işletme yok
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {anaVitrinItems.map((biz, index) => (
                  <div key={biz.id} className="p-4 flex items-center gap-4" data-testid={`ana-vitrin-${biz.id}`}>
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === 0 || saving}
                        onClick={() => handleMoveOrder(biz, 'ana', 'up')}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === anaVitrinItems.length - 1 || saving}
                        onClick={() => handleMoveOrder(biz, 'ana', 'down')}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{biz.name}</p>
                      <p className="text-muted-foreground text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {getCityName(biz.city_id)}
                      </p>
                    </div>
                    <Switch
                      checked={biz.ana_vitrin}
                      onCheckedChange={() => handleToggleAnaVitrin(biz)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dönen Vitrin */}
        <Card className="bg-card border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Dönen Vitrin
              <Badge variant="secondary" className="ml-auto">{donenVitrinItems.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {donenVitrinItems.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                Dönen Vitrin'de işletme yok
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {donenVitrinItems.map((biz, index) => (
                  <div key={biz.id} className="p-4 flex items-center gap-4" data-testid={`donen-vitrin-${biz.id}`}>
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === 0 || saving}
                        onClick={() => handleMoveOrder(biz, 'donen', 'up')}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === donenVitrinItems.length - 1 || saving}
                        onClick={() => handleMoveOrder(biz, 'donen', 'down')}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{biz.name}</p>
                      <p className="text-muted-foreground text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {getCityName(biz.city_id)}
                      </p>
                    </div>
                    <Switch
                      checked={biz.donen_vitrin}
                      onCheckedChange={() => handleToggleDonenVitrin(biz)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Available Businesses */}
      <Card className="bg-card border-white/10">
        <CardHeader className="border-b border-white/10">
          <CardTitle>Aktif İşletmeler</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredBusinesses.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              Aktif işletme yok
            </div>
          ) : (
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
              {filteredBusinesses.map(biz => (
                <div key={biz.id} className="p-4 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="font-medium">{biz.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {getCityName(biz.city_id)}
                      </Badge>
                      <Badge variant="outline" className="text-xs border-white/10">
                        {getCategoryName(biz.category_id)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Star className={`w-4 h-4 ${biz.ana_vitrin ? 'text-primary' : 'text-muted-foreground'}`} />
                      <Switch
                        checked={biz.ana_vitrin}
                        onCheckedChange={() => handleToggleAnaVitrin(biz)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className={`w-4 h-4 ${biz.donen_vitrin ? 'text-accent' : 'text-muted-foreground'}`} />
                      <Switch
                        checked={biz.donen_vitrin}
                        onCheckedChange={() => handleToggleDonenVitrin(biz)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
